import { FC, useEffect, useRef } from "react";

import MapControlCluster from "@/components/map/controls/MapControlCluster";
import MapFullScreenControl from "@/components/map/controls/MapFullScreenControl";
import MapRecenterControl from "@/components/map/controls/MapRecenterControl";
import MapZoomControl from "@/components/map/controls/MapZoomControl";
import BaseMapMenu from "@/components/map/menuControl/BaseMapMenu";
import { MAP_STYLES } from "@/constants/map";
import { useMap } from "@/hooks/map/core/useMapContext";

import AgenticAIDatasetPanel from "./AgenticAIDatasetPanel";

/**
 * Only the CARTO styles — the Mapbox ones in `MAP_STYLES` need a token this map
 * deliberately does without.
 */
const TOKEN_FREE_BASE_MAPS = {
  street: MAP_STYLES.street,
  no_labels: MAP_STYLES.no_labels,
  lite: MAP_STYLES.lite,
  no_buildings: MAP_STYLES.no_buildings
};

const DEFAULT_CENTER: [number, number] = [120.9213, -2.4];
const DEFAULT_ZOOM = 4;

type Props = {
  center?: [number, number];
  zoom?: number;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  isReportShortcutVisible?: boolean;
};

/**
 * The conversation view's window onto the shared application map.
 *
 * It hands its container to `MapProvider` rather than owning a maplibre
 * instance: the agent loads datasets through the same `useDataset` layer stack
 * the analysis page uses, so a second, private map would leave those layers
 * painting somewhere the user cannot see. Full screen arrives as props.
 */
const AgenticAIMap: FC<Props> = ({
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  isFullScreen,
  onToggleFullScreen,
  isReportShortcutVisible = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { map, setContainer, zoomLevel } = useMap();
  const [longitude, latitude] = center;

  // Hands the DOM node to the provider that owns the map. `setContainer` is a
  // fresh closure every provider render, so it stays out of the deps or this
  // would re-attach forever.
  useEffect(() => {
    if (containerRef.current) setContainer(containerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps the map camera aligned with the latest conversation view. `map` is
  // null until the provider finishes loading it, so this also applies the
  // initial view, once, at the moment the map appears.
  useEffect(() => {
    map?.jumpTo({ center: [longitude, latitude], zoom });
  }, [map, latitude, longitude, zoom]);

  return (
    <section
      role="region"
      aria-label="Map"
      className="relative h-full w-full rounded-3xl"
    >
      <div
        ref={containerRef}
        className="h-full w-full overflow-hidden rounded-3xl"
      />

      <AgenticAIDatasetPanel
        isReportShortcutVisible={isReportShortcutVisible}
      />

      <MapControlCluster>
        <MapRecenterControl
          className="mb-2"
          onRecenter={() => map?.flyTo({ center: [longitude, latitude], zoom })}
        />
        <MapFullScreenControl
          isFullScreen={isFullScreen}
          onToggle={onToggleFullScreen}
        />
        <MapZoomControl
          zoomLevel={zoomLevel}
          onZoomIn={() => map?.zoomIn()}
          onZoomOut={() => map?.zoomOut()}
        />
        <BaseMapMenu styles={TOKEN_FREE_BASE_MAPS} />
      </MapControlCluster>
    </section>
  );
};

export default AgenticAIMap;
